package com.trevorwiebe.timesheet.admin.usermanagement.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun UserManagementScreen(
    viewModel: UserManagementViewModel = koinViewModel(),
    onBack: () -> Unit,
) {
    val state by viewModel.state.collectAsState()

    val visibleUsers = if (state.showDeleted) state.deletedUsers else state.activeUsers

    Scaffold(
        topBar = {
            TopBar(
                title = "User Management",
                backIcon = { BackIcon() },
                onBack = onBack
            )
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = { viewModel.onEvent(UserManagementEvent.OnAddUserClick) },
                containerColor = MaterialTheme.colorScheme.tertiary,
                contentColor = MaterialTheme.colorScheme.onTertiary
            ) {
                Icon(Icons.Default.Add, contentDescription = "Add employee")
            }
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
        Column(
            modifier = Modifier
                .fillMaxSize()
        ) {
            ShowDeletedChipRow(
                showDeleted = state.showDeleted,
                deletedCount = state.deletedUsers.size,
                onToggle = { viewModel.onEvent(UserManagementEvent.OnShowDeletedToggled(it)) }
            )
            HorizontalDivider()

            if (state.isLoading) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
                }
            } else {
                LazyColumn(modifier = Modifier.fillMaxSize()) {
                    items(visibleUsers) { user ->
                        UserRow(
                            user = user,
                            onClick = { viewModel.onEvent(UserManagementEvent.OnEditUserClick(user)) }
                        )
                    }
                }
            }
        }

        NativeDialog(
            visible = state.error != null,
            title = "Something went wrong",
            message = state.error ?: "",
            confirmText = "OK",
            onConfirm = { viewModel.onEvent(UserManagementEvent.OnErrorShown) },
            onDismiss = { viewModel.onEvent(UserManagementEvent.OnErrorShown) }
        )

        if (state.showAddEditDialog) {
            AddEditUserDialog(
                editing = state.editingUser,
                isSaving = state.isSaving,
                isSendingPasswordReset = state.isSendingPasswordReset,
                passwordResetEmailSent = state.passwordResetEmailSent,
                currentUserId = state.currentUserId,
                ownerOperators = state.ownerOperators,
                onDismiss = { viewModel.onEvent(UserManagementEvent.OnDismissDialog) },
                onCreate = { viewModel.onEvent(it) },
                onUpdate = { viewModel.onEvent(it) },
                onSendPasswordReset = {
                    viewModel.onEvent(UserManagementEvent.OnSendPasswordResetEmail)
                },
                onDelete = { viewModel.onEvent(UserManagementEvent.OnDeleteUser) },
                onRestore = { viewModel.onEvent(UserManagementEvent.OnRestoreUser) }
            )
        }
        }
    }
}

@Composable
private fun ShowDeletedChipRow(
    showDeleted: Boolean,
    deletedCount: Int,
    onToggle: (Boolean) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Text(
            text = "Show",
            fontSize = 14.sp,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
        )
        FilterChip(
            selected = !showDeleted,
            onClick = { onToggle(false) },
            label = { Text("Active") },
            border = FilterChipDefaults.filterChipBorder(
                enabled = true,
                borderColor = MaterialTheme.colorScheme.tertiary,
                selected = !showDeleted
            ),
            colors = FilterChipDefaults.filterChipColors(
                containerColor = MaterialTheme.colorScheme.background,
                selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                selectedLabelColor = MaterialTheme.colorScheme.onTertiary
            )
        )
        FilterChip(
            selected = showDeleted,
            onClick = { onToggle(true) },
            label = { Text(if (deletedCount > 0) "Deactivated ($deletedCount)" else "Deactivated") },
            border = FilterChipDefaults.filterChipBorder(
                enabled = true,
                borderColor = MaterialTheme.colorScheme.tertiary,
                selected = showDeleted
            ),
            colors = FilterChipDefaults.filterChipColors(
                containerColor = MaterialTheme.colorScheme.background,
                selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                selectedLabelColor = MaterialTheme.colorScheme.onTertiary
            )
        )
    }
}

@Composable
private fun UserRow(
    user: DatabaseUserModel,
    onClick: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                modifier = Modifier.weight(1f),
                text = user.displayName.ifBlank { "(no name)" },
                fontSize = 16.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = user.role.label(),
                fontSize = 14.sp,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.tertiary
            )
        }
        Text(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = 16.dp, end = 16.dp, bottom = 12.dp),
            text = user.email.ifBlank { "(no email)" },
            fontSize = 14.sp,
            color = MaterialTheme.colorScheme.secondary,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
        HorizontalDivider()
    }
}
